import { useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser, useAuth } from "@clerk/react";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { trackPageView, identifyUser, resetUser } from "@/lib/analytics";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout/Layout";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing";
import UploadPage from "@/pages/upload";
import HistoryPage from "@/pages/history";
import StatusPage from "@/pages/status";
import ResultPage from "@/pages/result";
import BillingPage from "@/pages/billing";
import SubscriptionsPage from "@/pages/subscriptions";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import AdminEvaluationsPage from "@/pages/admin-evaluations";
import FoundingPage from "@/pages/founding";
import AdminFoundingPage from "@/pages/admin-founding";
import AdminJobsPage from "@/pages/admin-jobs";
import HelpPage from "@/pages/help";
import { CrispWidget } from "@/components/CrispWidget";

const queryClient = new QueryClient();

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath) ? path.slice(basePath.length) || "/" : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const clerkAppearance = {
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "hsl(239, 84%, 67%)",
    colorBackground: "hsl(222, 47%, 17%)",
    colorInputBackground: "hsl(222, 47%, 22%)",
    colorText: "hsl(210, 40%, 98%)",
    colorTextSecondary: "hsl(215, 20%, 65%)",
    colorInputText: "hsl(210, 40%, 98%)",
    colorNeutral: "hsl(215, 20%, 65%)",
    borderRadius: "0.375rem",
    fontFamily: "Inter, system-ui, sans-serif",
    fontFamilyButtons: "Inter, system-ui, sans-serif",
    fontSize: "0.9rem",
  },
  elements: {
    rootBox: "w-full",
    cardBox: "border border-slate-600 rounded-xl w-full overflow-hidden shadow-2xl ring-1 ring-slate-500/20",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: { style: { color: "#ffffff", fontWeight: "700", background: "none", WebkitTextFillColor: "#ffffff" } },
    headerSubtitle: { style: { color: "hsl(215, 20%, 65%)", background: "none", WebkitTextFillColor: "hsl(215, 20%, 65%)" } },
    socialButtonsBlockButtonText: { style: { color: "hsl(210, 40%, 98%)" } },
    formFieldLabel: "!text-slate-50 text-sm font-medium",
    badge: "!text-white !bg-indigo-500 !border-indigo-400",
    footerActionLink: { style: { color: "hsl(239, 84%, 72%)" } },
    footerActionText: { style: { color: "hsl(215, 20%, 65%)" } },
    dividerText: { style: { color: "hsl(215, 20%, 65%)" } },
    identityPreviewEditButton: { style: { color: "hsl(239, 84%, 72%)" } },
    formFieldSuccessText: { style: { color: "hsl(142, 76%, 56%)" } },
    alertText: { style: { color: "hsl(210, 40%, 98%)" } },
    logoBox: "flex items-center justify-center mb-2",
    logoImage: "h-10 w-10",
    socialButtonsBlockButton: "border-slate-700 hover:border-slate-500 bg-slate-800 hover:bg-slate-700 transition-colors",
    formButtonPrimary: "bg-indigo-500 hover:bg-indigo-600 text-white font-semibold transition-colors",
    formFieldInput: {
      className: "placeholder-slate-500 focus:border-indigo-500",
      style: {
        backgroundColor: "hsl(222, 47%, 22%)",
        color: "hsl(210, 40%, 98%)",
        WebkitTextFillColor: "hsl(210, 40%, 98%)",
        borderColor: "hsl(215, 25%, 35%)",
      },
    },
    footerAction: "bg-slate-800/50",
    dividerLine: "bg-slate-700",
    otpCodeFieldInput: "bg-slate-800 border-slate-600 text-white",
    formFieldRow: "gap-3",
    main: "gap-4",
  },
};

function AuthPageShell({ children, switchLink }: {
  children: React.ReactNode;
  switchLink?: { label: string; actionLabel: string; href: string };
}) {
  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/20">
        <div className="flex items-center gap-2">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-7 w-7 rounded-sm object-cover"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-sm text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </div>
        <a
          href={basePath || "/"}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors font-mono"
        >
          <ArrowLeft size={13} /> Back to home
        </a>
      </div>

      {/* Centered widget */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {children}
          {switchLink && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              {switchLink.label}{" "}
              <a href={switchLink.href} className="text-primary hover:text-primary/80 font-medium transition-colors">
                {switchLink.actionLabel} →
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SignInPage() {
  return (
    <AuthPageShell
      switchLink={{ label: "Don't have an account?", actionLabel: "Sign up free", href: `${basePath}/sign-up` }}
    >
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        forceRedirectUrl={`${basePath}/upload`}
      />
    </AuthPageShell>
  );
}

function SignUpPage() {
  return (
    <AuthPageShell
      switchLink={{ label: "Already have an account?", actionLabel: "Sign in", href: `${basePath}/sign-in` }}
    >
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
        forceRedirectUrl={`${basePath}/upload`}
      />
    </AuthPageShell>
  );
}

function HideClerkDevMode() {
  useEffect(() => {
    const hide = () => {
      document.querySelectorAll("a, button").forEach((el) => {
        if (el.textContent?.trim() === "Development mode") {
          const wrapper = el.closest('[class*="cl-"]') ?? el.parentElement;
          if (wrapper instanceof HTMLElement) wrapper.style.display = "none";
        }
      });
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

// Wire the generated API client to Clerk's getToken so all API calls include
// Authorization: Bearer <token> (required with Clerk React v5+)
function ClerkAuthSetup() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(getToken);
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  return null;
}

function PageViewTracker() {
  const [location] = useLocation();
  const prevLocationRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevLocationRef.current !== location) {
      prevLocationRef.current = location;
      trackPageView(basePath + (location === "/" ? "" : location));
    }
  }, [location]);

  return null;
}

function AnalyticsIdentifier() {
  const { user, isLoaded, isSignedIn } = useUser();
  const prevSignedInRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user) {
      identifyUser(user.id, user.primaryEmailAddress?.emailAddress);
      prevSignedInRef.current = true;
    } else if (prevSignedInRef.current === true && !isSignedIn) {
      resetUser();
      prevSignedInRef.current = false;
    } else if (!isSignedIn) {
      prevSignedInRef.current = false;
    }
  }, [isLoaded, isSignedIn, user]);

  return null;
}

// Sync user to DB on sign-in
function UserSyncer() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const synced = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || synced.current) return;
    synced.current = true;
    const email = user?.primaryEmailAddress?.emailAddress;
    (async () => {
      try {
        const token = await getToken();
        await fetch(`${basePath}/api/users/sync`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ email }),
          credentials: "include",
        });
      } catch {
        synced.current = false;
      }
    })();
  }, [isLoaded, isSignedIn, user, getToken]);

  return null;
}

function HomeRoute() {
  return <LandingPage />;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <>
      <Show when="signed-in">
        <Layout>
          <Component />
        </Layout>
      </Show>
      <Show when="signed-out">
        <Redirect to="/sign-in" />
      </Show>
    </>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      localization={{
        signIn: {
          start: {
            title: "Welcome back",
            subtitle: "Sign in to your Podcasts Abbreviated account",
          },
        },
        signUp: {
          start: {
            title: "Get started free",
            subtitle: "Try it on your podcast in minutes",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkAuthSetup />
        <ClerkQueryClientCacheInvalidator />
        <UserSyncer />
        <CrispWidget />
        <HideClerkDevMode />
        <PageViewTracker />
        <AnalyticsIdentifier />
        <TooltipProvider>
          <Switch>
            <Route path="/" component={HomeRoute} />
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/help" component={HelpPage} />
            <Route path="/founding" component={FoundingPage} />
            <Route path="/admin/founding" component={AdminFoundingPage} />
            <Route path="/sign-in/*?" component={SignInPage} />
            <Route path="/sign-up/*?" component={SignUpPage} />
            <Route path="/admin/evaluations" component={AdminEvaluationsPage} />
            <Route path="/admin/evaluations/:jobId" component={AdminEvaluationsPage} />
            <Route path="/admin/founding" component={AdminFoundingPage} />
            <Route path="/admin/jobs" component={AdminJobsPage} />
            <Route path="/upload" component={() => <Layout><UploadPage /></Layout>} />
            <Route path="/jobs" component={() => <ProtectedRoute component={HistoryPage} />} />
            <Route path="/billing" component={() => <ProtectedRoute component={BillingPage} />} />
            <Route path="/subscriptions" component={() => <ProtectedRoute component={SubscriptionsPage} />} />
            <Route
              path="/jobs/:id/result"
              component={() => <ProtectedRoute component={ResultPage} />}
            />
            <Route
              path="/jobs/:id"
              component={() => <ProtectedRoute component={StatusPage} />}
            />
            <Route component={NotFound} />
          </Switch>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
